import { Vec3, type Track } from '@mindcloudxai_3d/core';
import type { RaceKeyframe, LookMode } from './types';

/** 生成一个简单的随机小写字母+数字的 8 位 ID */
export function generateId(): string {
  return `kf-${Math.random().toString(36).substring(2, 10)}`;
}

/**
 * 将编辑态关键帧列表转换为播放器识别的中性 Track。
 * 对 keyframes 按 time 进行升序重排，并展平 position / target / fov 数组。
 *
 * 当相邻关键帧的 look 方向接近反向时（dot < 0），在中间插入若干过渡帧，
 * 使 target 沿球面弧线平滑旋转，避免样条插值穿越相机位置导致 180° 翻转。
 */
export function raceKeyframesToTrack(
  keyframes: RaceKeyframe[],
  duration: number,
  loopMode: 'none' | 'repeat' | 'pingpong' = 'repeat',
  interpolation: 'step' | 'spline' = 'step',
  smoothness: number = 1,
): Track {
  // 确保按时间单调递增
  const sorted = keyframes.toSorted((a, b) => a.time - b.time);
  if (sorted.length < 2) {
    const times = sorted.map((kf) => kf.time);
    const position: number[] = [];
    const target: number[] = [];
    const fov: number[] = [];
    const up: number[] = [];
    let hasUp = false;
    for (const kf of sorted) {
      position.push(kf.position.x, kf.position.y, kf.position.z);
      target.push(kf.target.x, kf.target.y, kf.target.z);
      fov.push(kf.fov);
      if (kf.up) {
        up.push(kf.up.x, kf.up.y, kf.up.z);
        hasUp = true;
      } else {
        up.push(0, 1, 0);
      }
    }
    return {
      name: 'race-track',
      duration,
      loopMode,
      interpolation,
      smoothness,
      keyframes: { times, values: { position, target, fov, up: hasUp ? up : undefined } },
    };
  }

  // 保存原始用户关键帧时间，供可视化时区分用户帧与平滑插入帧
  const markerTimes = sorted.map((kf) => kf.time);

  // 检测 look 方向翻转并插入过渡帧
  const densified = densifyLookFlips(sorted);

  const times = densified.map((kf) => kf.time);
  const position: number[] = [];
  const target: number[] = [];
  const fov: number[] = [];
  const up: number[] = [];
  let hasUp = false;

  for (const kf of densified) {
    position.push(kf.position.x, kf.position.y, kf.position.z);
    target.push(kf.target.x, kf.target.y, kf.target.z);
    fov.push(kf.fov);
    if (kf.up) {
      up.push(kf.up.x, kf.up.y, kf.up.z);
      hasUp = true;
    } else {
      up.push(0, 1, 0);
    }
  }

  return {
    name: 'race-track',
    duration,
    loopMode,
    interpolation,
    smoothness,
    keyframes: {
      times,
      values: {
        position,
        target,
        fov,
        up: hasUp ? up : undefined,
      },
    },
    markerTimes,
  };
}

/**
 * 检测相邻关键帧的 look 方向是否接近反向，若是则插入过渡帧。
 * 过渡帧的 target 通过 SLERP 在两个 look 方向之间平滑旋转，
 * position 和 fov 线性插值，时间均匀分配。
 */
function densifyLookFlips(sorted: RaceKeyframe[]): RaceKeyframe[] {
  const result: RaceKeyframe[] = [];
  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);
    if (i >= sorted.length - 1) break;

    const a = sorted[i];
    const b = sorted[i + 1];

    // 计算 look 方向
    const dirA = lookDir(a);
    const dirB = lookDir(b);
    const dot = dirA.x * dirB.x + dirA.y * dirB.y + dirA.z * dirB.z;

    // 方向接近反向时插入过渡帧
    if (dot < 0) {
      // 插入帧数取决于翻转程度，最多 8 帧
      const insertCount = Math.max(2, Math.ceil((1 - dot) * 4));
      const distA = lookDist(a);
      const distB = lookDist(b);
      for (let j = 1; j < insertCount; j++) {
        const t = j / insertCount;
        const dir = slerpDir(dirA, dirB, t);
        const dist = distA * (1 - t) + distB * t;
        const pos = lerpVec3(a.position, b.position, t);
        const tgt = new Vec3(pos.x + dir.x * dist, pos.y + dir.y * dist, pos.z + dir.z * dist);
        result.push({
          id: generateId(),
          time: a.time + (b.time - a.time) * t,
          position: pos,
          target: tgt,
          fov: a.fov * (1 - t) + b.fov * t,
          up: interpolateUp(a.up, b.up, t),
        });
      }
    }
  }
  return result;
}

function lookDir(kf: RaceKeyframe): Vec3 {
  const dx = kf.target.x - kf.position.x;
  const dy = kf.target.y - kf.position.y;
  const dz = kf.target.z - kf.position.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-6) return new Vec3(0, 0, -1);
  return new Vec3(dx / len, dy / len, dz / len);
}

function lookDist(kf: RaceKeyframe): number {
  const dx = kf.target.x - kf.position.x;
  const dy = kf.target.y - kf.position.y;
  const dz = kf.target.z - kf.position.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** 在两个关键帧的 up 向量之间做 SLERP 插值；任一缺失时返回 undefined */
function interpolateUp(a: Vec3 | undefined, b: Vec3 | undefined, t: number): Vec3 | undefined {
  if (!a && !b) return undefined;
  if (!a) return b;
  if (!b) return a;
  return slerpDir(a, b, t);
}

function lerpVec3(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  t: number,
): Vec3 {
  return new Vec3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);
}

/**
 * 将中性 Track 转换为编辑态关键帧列表。
 * 主要用于从场景自带的 COLMAP 运镜中加载原始帧，或者 debug 数据流。
 */
export function trackToRaceKeyframes(track: Track): RaceKeyframe[] {
  const times = track.keyframes.times;
  const { position, target, fov, up } = track.keyframes.values;
  const keyframes: RaceKeyframe[] = [];

  const count = times.length;
  for (let i = 0; i < count; i++) {
    const time = times[i];
    const posIdx = i * 3;
    const tgtIdx = i * 3;

    // 容错处理
    const pos = {
      x: position[posIdx] ?? 0,
      y: position[posIdx + 1] ?? 0,
      z: position[posIdx + 2] ?? 0,
    };
    const tgt = {
      x: target[tgtIdx] ?? 0,
      y: target[tgtIdx + 1] ?? 0,
      z: target[tgtIdx + 2] ?? 0,
    };

    const kfUp =
      up && up[posIdx] !== undefined && up[posIdx + 1] !== undefined && up[posIdx + 2] !== undefined
        ? { x: up[posIdx], y: up[posIdx + 1], z: up[posIdx + 2] }
        : undefined;

    keyframes.push({
      id: generateId(),
      time,
      position: new Vec3(pos.x, pos.y, pos.z),
      target: new Vec3(tgt.x, tgt.y, tgt.z),
      fov: fov?.[i] ?? 60,
      up: kfUp ? new Vec3(kfUp.x, kfUp.y, kfUp.z) : undefined,
    });
  }

  return keyframes.sort((a, b) => a.time - b.time);
}

/**
 * 时间轴捕获帧逻辑：
 * - 如果在 time 容差范围内已存在关键帧，则认为是“覆盖该帧”位姿。
 * - 如果不存在，则在该时间点“插入新帧”，返回更新后的 keyframes 数组。
 * 容差 tolerance 默认为 0.05 秒（即 50 毫秒）。
 */
export function captureOrUpdateKeyframe(
  keyframes: RaceKeyframe[],
  newFrame: Omit<RaceKeyframe, 'id'>,
  tolerance = 0.05,
): { keyframes: RaceKeyframe[]; activeId: string } {
  const sorted = keyframes.toSorted((a, b) => a.time - b.time);

  // 查找是否有极其贴近该时间的现有关键帧
  const existingIndex = sorted.findIndex((kf) => Math.abs(kf.time - newFrame.time) <= tolerance);

  if (existingIndex !== -1) {
    // 覆盖已有关键帧
    const targetKframe = sorted[existingIndex];
    const updated: RaceKeyframe = {
      ...targetKframe,
      // 保持原有 id，覆盖姿态与 FOV
      position: newFrame.position,
      target: newFrame.target,
      fov: newFrame.fov,
      up: newFrame.up,
      // 微调一下时间对齐到正好是触发时间，确保拖拽流畅
      time: newFrame.time,
    };
    sorted[existingIndex] = updated;
    return { keyframes: sorted, activeId: targetKframe.id };
  } else {
    // 创建新关键帧
    const id = generateId();
    const created: RaceKeyframe = {
      id,
      ...newFrame,
    };
    sorted.push(created);
    sorted.sort((a, b) => a.time - b.time);
    return { keyframes: sorted, activeId: id };
  }
}

// ─── 轨道优化：Catmull-Rom 补帧 + 迭代平滑 + 等弧长重采样 ──────────────────────

const CATMULL_ROM_DENSITY = 12;
const SMOOTHING_ITERATIONS = 3;
const DEFAULT_OPTIMIZE_TARGET = 60;

function catmullRomPoint(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, t: number): Vec3 {
  const t2 = t * t;
  const t3 = t2 * t;
  return new Vec3(
    0.5 *
      (2 * p1.x +
        (-p0.x + p2.x) * t +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    0.5 *
      (2 * p1.y +
        (-p0.y + p2.y) * t +
        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
    0.5 *
      (2 * p1.z +
        (-p0.z + p2.z) * t +
        (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 +
        (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
  );
}

function densify(points: Vec3[], density: number): Vec3[] {
  if (points.length < 3) return points.slice();
  const result: Vec3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    for (let j = 0; j < density; j++) {
      result.push(catmullRomPoint(p0, p1, p2, p3, j / density));
    }
  }
  result.push(points[points.length - 1]);
  return result;
}

function iterativeSmooth(points: Vec3[], iterations: number): Vec3[] {
  if (points.length < 3) return points;
  let current = points;
  for (let iter = 0; iter < iterations; iter++) {
    const next: Vec3[] = [current[0]];
    for (let i = 1; i < current.length - 1; i++) {
      const p0 = current[i - 1];
      const p1 = current[i];
      const p2 = current[i + 1];
      next.push(
        new Vec3(
          p0.x * 0.25 + p1.x * 0.5 + p2.x * 0.25,
          p0.y * 0.25 + p1.y * 0.5 + p2.y * 0.25,
          p0.z * 0.25 + p1.z * 0.5 + p2.z * 0.25,
        ),
      );
    }
    next.push(current[current.length - 1]);
    current = next;
  }
  return current;
}

function arcLengthResample(points: Vec3[], count: number): Vec3[] {
  if (points.length < 2 || count < 2) return points.slice(0, Math.max(count, 1));
  const cumLen = [0];
  for (let i = 1; i < points.length; i++) {
    cumLen.push(cumLen[i - 1] + points[i].distanceTo(points[i - 1]));
  }
  const totalLen = cumLen[cumLen.length - 1];
  if (totalLen <= 1e-6) return points.slice(0, count);

  const result: Vec3[] = [];
  let seg = 0;
  for (let i = 0; i < count; i++) {
    const targetDist = (i / (count - 1)) * totalLen;
    while (seg < cumLen.length - 2 && cumLen[seg + 1] < targetDist) seg++;
    const segLen = cumLen[seg + 1] - cumLen[seg];
    const t = segLen > 1e-10 ? (targetDist - cumLen[seg]) / segLen : 0;
    const a = points[seg];
    const b = points[Math.min(seg + 1, points.length - 1)];
    result.push(new Vec3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t));
  }
  return result;
}

/**
 * 计算路径质心（XZ 平面）
 */
function computeCentroidXZ(positions: Vec3[]): Vec3 {
  let cx = 0,
    cy = 0,
    cz = 0;
  for (const p of positions) {
    cx += p.x;
    cy += p.y;
    cz += p.z;
  }
  const n = positions.length || 1;
  return new Vec3(cx / n, cy / n, cz / n);
}

/**
 * 三点外心（XZ 平面），用于估计局部曲率中心。
 * 返回 null 表示三点近平行，无法拟合圆。
 */
function circumcenterXZ(a: Vec3, b: Vec3, c: Vec3): { center: Vec3; radius: number } | null {
  const d = 2 * (a.x * (b.z - c.z) + b.x * (c.z - a.z) + c.x * (a.z - b.z));
  if (Math.abs(d) < 1e-10) return null;

  const aSq = a.x * a.x + a.z * a.z;
  const bSq = b.x * b.x + b.z * b.z;
  const cSq = c.x * c.x + c.z * c.z;

  const cx = (aSq * (b.z - c.z) + bSq * (c.z - a.z) + cSq * (a.z - b.z)) / d;
  const cz = (aSq * (c.x - b.x) + bSq * (a.x - c.x) + cSq * (b.x - a.x)) / d;

  const radius = Math.sqrt((a.x - cx) ** 2 + (a.z - cz) ** 2);
  return { center: new Vec3(cx, 0, cz), radius };
}

/** 转角阈值（弧度），低于此值视为直线段，高于此值逐渐向曲率中心看 */
const CURVATURE_THRESHOLD = 0.25; // ~14°
/** 平滑过渡范围：从阈值到 PI/4 之间线性混合 forward → center */
const CURVATURE_FULL = Math.PI / 4; // 45°

/**
 * auto 模式：对每个采样点用局部窗口估计转角，
 * 转角大时朝向局部曲率中心（内切线），转角小时沿切线前方。
 * 直线段和弯道段之间平滑过渡，避免视线突变。
 */
function autoLookTargets(positions: Vec3[], targetDistance: number): Vec3[] {
  const n = positions.length;
  // 窗口大小：取总点数的 8%，至少 3
  const windowSize = Math.max(3, Math.floor(n * 0.08));

  return positions.map((pos, i) => {
    const lo = Math.max(0, i - windowSize);
    const hi = Math.min(n - 1, i + windowSize);

    // 切线方向（XZ 平面）
    const tangentX = positions[hi].x - positions[lo].x;
    const tangentZ = positions[hi].z - positions[lo].z;
    const tangentLen = Math.sqrt(tangentX * tangentX + tangentZ * tangentZ);
    if (tangentLen < 1e-6) {
      return pos.add(Vec3.FORWARD.scale(targetDistance));
    }
    const tx = tangentX / tangentLen;
    const tz = tangentZ / tangentLen;

    // forward 方向 target
    const forwardTarget = new Vec3(pos.x + tx * targetDistance, pos.y, pos.z + tz * targetDistance);

    if (hi - lo < 2) return forwardTarget;

    // 估计转角：前后切线的夹角
    const beforeX = pos.x - positions[lo].x;
    const beforeZ = pos.z - positions[lo].z;
    const afterX = positions[hi].x - pos.x;
    const afterZ = positions[hi].z - pos.z;
    const beforeLen = Math.sqrt(beforeX * beforeX + beforeZ * beforeZ);
    const afterLen = Math.sqrt(afterX * afterX + afterZ * afterZ);
    if (beforeLen < 1e-6 || afterLen < 1e-6) return forwardTarget;

    const dot = Math.max(
      -1,
      Math.min(1, (beforeX * afterX + beforeZ * afterZ) / (beforeLen * afterLen)),
    );
    const turningAngle = Math.acos(dot);

    if (turningAngle < CURVATURE_THRESHOLD) return forwardTarget;

    // 弯道段：计算局部曲率中心
    const cc = circumcenterXZ(positions[lo], pos, positions[hi]);
    if (!cc) return forwardTarget;

    // 从当前点指向曲率中心的方向
    const dirX = cc.center.x - pos.x;
    const dirZ = cc.center.z - pos.z;
    const dirLen = Math.sqrt(dirX * dirX + dirZ * dirZ);
    if (dirLen < 1e-6) return forwardTarget;

    const cx = dirX / dirLen;
    const cz = dirZ / dirLen;
    const centerTarget = new Vec3(pos.x + cx * targetDistance, pos.y, pos.z + cz * targetDistance);

    // 转角越大越偏向 center，在 [threshold, full] 区间线性混合
    const blend = Math.min(
      1,
      (turningAngle - CURVATURE_THRESHOLD) / (CURVATURE_FULL - CURVATURE_THRESHOLD),
    );
    return new Vec3(
      forwardTarget.x * (1 - blend) + centerTarget.x * blend,
      pos.y,
      forwardTarget.z * (1 - blend) + centerTarget.z * blend,
    );
  });
}

/**
 * 球面线性插值（SLERP）两个单位向量。
 * 当方向接近反向时（dot < -0.9995），取垂直参考轴做旋转，避免退化。
 */
function slerpDir(a: Vec3, b: Vec3, t: number): Vec3 {
  let dot = a.x * b.x + a.y * b.y + a.z * b.z;
  dot = Math.max(-1, Math.min(1, dot));

  if (dot > 0.9995) {
    // 方向几乎一致，用 lerp 即可
    const len = Math.sqrt(
      (a.x + (b.x - a.x) * t) ** 2 + (a.y + (b.y - a.y) * t) ** 2 + (a.z + (b.z - a.z) * t) ** 2,
    );
    if (len < 1e-10) return new Vec3(a.x, a.y, a.z);
    return new Vec3(
      (a.x + (b.x - a.x) * t) / len,
      (a.y + (b.y - a.y) * t) / len,
      (a.z + (b.z - a.z) * t) / len,
    );
  }

  if (dot < -0.9995) {
    // 方向几乎相反：绕垂直轴旋转 180°，用 (0,1,0) 或 (1,0,0) 做参考
    const ref = Math.abs(a.y) < 0.9 ? new Vec3(0, 1, 0) : new Vec3(1, 0, 0);
    // perp = ref - a*(a·ref)，归一化
    const proj = a.x * ref.x + a.y * ref.y + a.z * ref.z;
    const perp = new Vec3(ref.x - a.x * proj, ref.y - a.y * proj, ref.z - a.z * proj);
    const pl = Math.sqrt(perp.x * perp.x + perp.y * perp.y + perp.z * perp.z);
    if (pl < 1e-10) return new Vec3(a.x, a.y, a.z);
    const px = perp.x / pl,
      py = perp.y / pl,
      pz = perp.z / pl;
    // t=0.5 时旋转 90°
    const angle = t * Math.PI;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vec3(a.x * cos + px * sin, a.y * cos + py * sin, a.z * cos + pz * sin);
  }

  const theta = Math.acos(dot);
  const sinTheta = Math.sin(theta);
  const w1 = Math.sin((1 - t) * theta) / sinTheta;
  const w2 = Math.sin(t * theta) / sinTheta;
  return new Vec3(a.x * w1 + b.x * w2, a.y * w1 + b.y * w2, a.z * w1 + b.z * w2);
}

/**
 * forward 模式的方向感知 target 平滑：
 * 提取每帧的 look 方向（target - position），对方向做 Catmull-Rom 密集采样
 * 后用 SLERP 在弧长重采样点上插值，再按平滑后的 look 距离重建 target。
 * 相比直接对 target 位置做平滑，方向插值在朝向突变（如向上看→向下看）时
 * 不会产生大幅摇摆或翻转。
 */
function directionAwareSmoothTargets(
  sorted: RaceKeyframe[],
  smoothPositions: Vec3[],
  targetCount: number,
): Vec3[] {
  // 提取原始 look 方向和距离
  const srcDirs: Vec3[] = [];
  const srcDists: number[] = [];
  for (const kf of sorted) {
    const dx = kf.target.x - kf.position.x;
    const dy = kf.target.y - kf.position.y;
    const dz = kf.target.z - kf.position.z;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 1e-6) {
      srcDirs.push(new Vec3(0, 0, -1));
      srcDists.push(10);
    } else {
      srcDirs.push(new Vec3(dx / len, dy / len, dz / len));
      srcDists.push(len);
    }
  }

  // 对 look 距离做线性插值到 targetCount 个点
  const smoothDists: number[] = [];
  for (let i = 0; i < targetCount; i++) {
    const t = i / (targetCount - 1);
    const srcIdx = t * (sorted.length - 1);
    const lo = Math.floor(srcIdx);
    const hi = Math.min(lo + 1, sorted.length - 1);
    const frac = srcIdx - lo;
    smoothDists.push(srcDists[lo] * (1 - frac) + srcDists[hi] * frac);
  }

  // 对方向做 SLERP 插值到 targetCount 个点
  const smoothDirs: Vec3[] = [];
  for (let i = 0; i < targetCount; i++) {
    const t = i / (targetCount - 1);
    const srcIdx = t * (sorted.length - 1);
    const lo = Math.floor(srcIdx);
    const hi = Math.min(lo + 1, sorted.length - 1);
    const frac = srcIdx - lo;
    smoothDirs.push(slerpDir(srcDirs[lo], srcDirs[hi], frac));
  }

  // 用平滑后的方向 + 距离 + 平滑后的 position 重建 target
  return smoothPositions.map(
    (pos, i) =>
      new Vec3(
        pos.x + smoothDirs[i].x * smoothDists[i],
        pos.y + smoothDirs[i].y * smoothDists[i],
        pos.z + smoothDirs[i].z * smoothDists[i],
      ),
  );
}

/**
 * 对用户手动标记的关键帧做平滑优化：
 * 1. 提取 position 序列
 * 2. Catmull-Rom 高密度补帧
 * 3. 迭代移动平均消除抖动
 * 4. 等弧长重采样到 targetCount 个点，保证匀速
 * 5. 根据 lookMode 生成 target：
 *    - forward: 沿路径切线方向 look-ahead（外切线，适合直线行进）
 *    - center:  朝向路径质心（内切线，过山车效果，适合环形轨道）
 *    - auto:    自动检测弯道段，弯道朝向局部曲率中心，直线段沿切线前方
 * 6. 在 duration 上均匀分配时间
 *
 * 返回优化后的关键帧列表；调用方负责备份原始帧以便还原。
 */
export function optimizeKeyframes(
  keyframes: RaceKeyframe[],
  duration: number,
  targetCount = DEFAULT_OPTIMIZE_TARGET,
  lookMode: LookMode = 'auto',
): RaceKeyframe[] {
  const sorted = keyframes.toSorted((a, b) => a.time - b.time);
  if (sorted.length < 2 || targetCount < 2) return sorted;

  const positions = sorted.map((kf) => kf.position);

  // 对 position 做补帧 → 平滑 → 等弧长重采样
  const smoothPositions = arcLengthResample(
    iterativeSmooth(densify(positions, CATMULL_ROM_DENSITY), SMOOTHING_ITERATIONS),
    targetCount,
  );

  // 根据 lookMode 生成 target
  const hasUpVectors = sorted.some((kf) => kf.up);
  let smoothTargets: Vec3[];
  if (lookMode === 'center') {
    // 朝向路径质心：过山车内切线效果
    const centroid = computeCentroidXZ(smoothPositions);
    smoothTargets = smoothPositions.map((pos) => {
      const dir = new Vec3(centroid.x - pos.x, 0, centroid.z - pos.z);
      const dist = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
      if (dist < 1e-6) {
        return pos.add(Vec3.FORWARD.scale(10));
      }
      const dirNorm = new Vec3(dir.x / dist, 0, dir.z / dist);
      return new Vec3(pos.x + dirNorm.x * 10, pos.y, pos.z + dirNorm.z * 10);
    });
  } else if (lookMode === 'auto' && !hasUpVectors) {
    // 自动检测弯道：弯道段朝向局部曲率中心，直线段沿切线前方
    // 仅在无 up 向量（纯水平运镜）时使用；有 up 向量时 autoLookTargets
    // 会把 target 强制到水平面，丢失垂直朝向并与 up 产生冲突。
    smoothTargets = autoLookTargets(smoothPositions, 10);
  } else {
    // forward 模式或有 up 向量时：对用户指定的 target 做方向感知平滑
    // 直接对 target 位置做 Catmull-Rom 平滑会在朝向突变时产生大幅摇摆，
    // 改为提取 look 方向（target - position）做球面插值，再按原始距离重建 target
    smoothTargets = directionAwareSmoothTargets(sorted, smoothPositions, targetCount);
  }

  // fov 线性插值到 targetCount 个点
  const smoothFov: number[] = [];
  for (let i = 0; i < targetCount; i++) {
    const t = i / (targetCount - 1);
    const srcIdx = t * (sorted.length - 1);
    const lo = Math.floor(srcIdx);
    const hi = Math.min(lo + 1, sorted.length - 1);
    const frac = srcIdx - lo;
    smoothFov.push(sorted[lo].fov * (1 - frac) + sorted[hi].fov * frac);
  }

  // up 向量 SLERP 插值到 targetCount 个点（仅当存在 up 时）
  // dir 和 up 是独立插值的，中间可能出现两者接近平行的情况，
  // 会导致 Camera.look() 中 cross(up, back) 退化。插值后对 up 做
  // Gram-Schmidt 正交化，确保 up ⊥ dir。
  const hasUp = sorted.some((kf) => kf.up);
  const smoothUp: (Vec3 | undefined)[] = [];
  if (hasUp) {
    for (let i = 0; i < targetCount; i++) {
      const t = i / (targetCount - 1);
      const srcIdx = t * (sorted.length - 1);
      const lo = Math.floor(srcIdx);
      const hi = Math.min(lo + 1, sorted.length - 1);
      const frac = srcIdx - lo;
      const up = interpolateUp(
        sorted[lo].up ?? new Vec3(0, 1, 0),
        sorted[hi].up ?? new Vec3(0, 1, 0),
        frac,
      );
      if (up) {
        // dir = target - position，归一化
        const dx = smoothTargets[i].x - smoothPositions[i].x;
        const dy = smoothTargets[i].y - smoothPositions[i].y;
        const dz = smoothTargets[i].z - smoothPositions[i].z;
        const dlen = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dlen > 1e-6) {
          const ndx = dx / dlen,
            ndy = dy / dlen,
            ndz = dz / dlen;
          const dot = up.x * ndx + up.y * ndy + up.z * ndz;
          // up_orth = up - dir * (up · dir)
          const ox = up.x - ndx * dot;
          const oy = up.y - ndy * dot;
          const oz = up.z - ndz * dot;
          const olen = Math.sqrt(ox * ox + oy * oy + oz * oz);
          if (olen > 1e-6) {
            smoothUp.push(new Vec3(ox / olen, oy / olen, oz / olen));
          } else {
            // up 与 dir 完全平行，用与 dir 不平行的参考轴叉积构造正交 up
            const ref = Math.abs(ndy) < 0.9 ? new Vec3(0, 1, 0) : new Vec3(1, 0, 0);
            // perp = ref - dir * (ref · dir)，再归一化
            const rdot = ref.x * ndx + ref.y * ndy + ref.z * ndz;
            const px = ref.x - ndx * rdot;
            const py = ref.y - ndy * rdot;
            const pz = ref.z - ndz * rdot;
            const plen = Math.sqrt(px * px + py * py + pz * pz);
            smoothUp.push(new Vec3(px / plen, py / plen, pz / plen));
          }
        } else {
          smoothUp.push(up);
        }
      } else {
        smoothUp.push(undefined);
      }
    }
  }

  // 均匀分配时间
  const result: RaceKeyframe[] = [];
  for (let i = 0; i < targetCount; i++) {
    result.push({
      id: generateId(),
      time: (i / (targetCount - 1)) * duration,
      position: smoothPositions[i],
      target: smoothTargets[i],
      fov: smoothFov[i],
      up: smoothUp[i],
    });
  }

  return result;
}

/**
 * 拖动关键帧时间点：
 * 保证拖动时间在 0 ~ duration 之间，并处理好碰撞/排序关系。
 */
export function moveKeyframeTime(
  keyframes: RaceKeyframe[],
  id: string,
  newTime: number,
  duration: number,
  tolerance = 0.01,
): RaceKeyframe[] {
  const clampedTime = Math.max(0, Math.min(newTime, duration));

  return keyframes
    .map((kf) => {
      if (kf.id !== id) return kf;

      // 检查是否与其他帧发生时间重合，如果有，则给一个极微小的偏移
      let finalTime = clampedTime;
      const hasCollision = keyframes.some(
        (other) => kf.id !== other.id && Math.abs(other.time - clampedTime) < tolerance,
      );
      if (hasCollision) {
        // 存在碰撞时，进行极微量偏移避免时间轴上两个物理圆点重叠在同一个 time 字段下
        finalTime = clampedTime + (newTime > kf.time ? tolerance : -tolerance);
        finalTime = Math.max(0, Math.min(finalTime, duration));
      }

      return {
        ...kf,
        time: finalTime,
      };
    })
    .sort((a, b) => a.time - b.time);
}
